import { useState } from "react";
import { comics, categories, faqs } from "@/mocks/comics";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function Home() {
  const featuredComics = comics.filter((c) => c.featured);
  const latestComics = [...comics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden" aria-labelledby="hero-heading">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20art%20style%20epic%20fantasy%20landscape%20with%20dramatic%20clouds%20and%20golden%20light%20rays%2C%20manga%20illustration%20style%2C%20vibrant%20warm%20colors%2C%20artistic%20abstract%20background%20with%20ink%20wash%20effects%2C%20cinematic%20composition%2C%20no%20text%2C%20dreamy%20atmosphere&width=1920&height=1080&seq=hero-bg-hanman&orientation=landscape"
              alt="韩漫大全下拉式免费阅读背景"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
          </div>
          <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 text-center">
            <h1 id="hero-heading" className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
              韩漫大全下拉式免费
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-4 max-w-2xl mx-auto leading-relaxed">
              无删减韩漫下拉式在线 — 韩漫画网
            </p>
            <p className="text-sm md:text-base text-white/70 mb-8 max-w-xl mx-auto leading-relaxed">
              韩漫画网支持下拉式免费阅读，韩漫大全漫画无删减下拉式在线观看，界面优化体验舒适，帮助您高效享受韩漫魅力。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to={`/category/${categories[0].slug}`}
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-play-circle-line text-lg"></i>
                </div>
                开始阅读
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-white/15 backdrop-blur-sm text-white rounded-full text-sm font-semibold hover:bg-white/25 transition-all duration-300 whitespace-nowrap cursor-pointer border border-white/20"
              >
                了解更多
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-lg"></i>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-background-50 border-b border-background-200/70" aria-label="平台数据">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: "10,000+", label: "漫画作品" },
                { value: "500万+", label: "月活用户" },
                { value: "99.9%", label: "无删减率" },
                { value: "24h", label: "最快更新" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-1" style={{ fontFamily: "var(--font-heading)" }}>
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Section */}
        <section className="bg-background-50 py-14" aria-labelledby="category-heading">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
              <div>
                <h2 id="category-heading" className="text-sm font-semibold text-foreground-600 uppercase tracking-wide mb-2">分类浏览</h2>
                <h3 className="text-2xl md:text-3xl font-bold text-foreground-950 italic" style={{ fontFamily: "var(--font-heading)" }}>
                  探索你喜欢的类型
                </h3>
              </div>
              <p className="text-sm text-foreground-600 max-w-md">
                涵盖奇幻冒险、恋爱浪漫、武侠格斗、悬疑惊悚等多种类型，总有一款适合你
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-3 p-6 rounded-lg bg-background-100 hover:bg-primary-500 group transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 group-hover:bg-white/20 transition-colors">
                    <i className={`${cat.icon} text-xl text-primary-500 group-hover:text-white transition-colors`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-950 group-hover:text-white whitespace-nowrap transition-colors">{cat.name}</span>
                  <span className="text-xs text-foreground-600 group-hover:text-white/70 transition-colors">{cat.count}部</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Comics */}
        <section id="featured" className="bg-background-100/70 py-14" aria-labelledby="featured-heading">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
              <div>
                <h2 className="text-sm font-semibold text-foreground-600 uppercase tracking-wide mb-2">热门推荐</h2>
                <h3 id="featured-heading" className="text-2xl md:text-3xl font-bold text-foreground-950 italic" style={{ fontFamily: "var(--font-heading)" }}>
                  千万读者都在追的韩漫
                </h3>
              </div>
              <p className="text-sm text-foreground-600 max-w-md">
                精选高分热门韩漫，每一部都是经过千万读者验证的口碑佳作
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop="">
              {featuredComics.map((comic, idx) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.slug}`}
                  className="group cursor-pointer"
                >
                  <div className="bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:scale-[1.02]">
                    <div className="relative h-[320px] overflow-hidden">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} - 韩漫下拉式免费阅读`}
                        title={`${comic.title} - 无删减韩漫下拉式在线`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading={idx === 0 ? "eager" : "lazy"}
                      />
                      <div className="absolute top-3 left-3">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-semibold rounded-full">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-[10px]"></i>
                          </div>
                          {comic.rating}
                        </span>
                      </div>
                      <div className="absolute top-3 right-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${comic.status === "连载中" ? "bg-primary-500/90 text-background-50" : "bg-foreground-700/80 text-background-50"}`}>
                          {comic.status}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-accent-500 font-medium mb-1.5">{comic.category}</p>
                      <h4 className="text-base font-semibold text-foreground-950 mb-1 group-hover:text-primary-500 transition-colors" style={{ fontFamily: "var(--font-heading)" }}>
                        {comic.title}
                      </h4>
                      <p className="text-xs text-foreground-600 line-clamp-2 mb-3">{comic.description}</p>
                      <div className="flex items-center justify-between text-xs text-foreground-600">
                        <span>{comic.author}</span>
                        <span>{comic.chapters}话</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
              {/* Visual break card */}
              <div className="hidden lg:flex items-center justify-center bg-foreground-950 rounded-lg p-8 min-h-[320px]">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-500/20">
                    <i className="ri-fire-line text-2xl text-accent-500"></i>
                  </div>
                  <p className="text-accent-500 font-semibold text-lg mb-2">每日更新</p>
                  <p className="text-foreground-400 text-sm">追更不停歇</p>
                  <p className="text-foreground-500 text-xs mt-1">韩漫大全随时看</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-background-50 py-14" aria-labelledby="cta-heading">
          <div className="max-w-[1000px] mx-auto px-4 md:px-6 text-center">
            <div className="relative w-full rounded-2xl overflow-hidden mb-10 h-[280px] md:h-[360px]">
              <img
                src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20scroll%20reading%20concept%2C%20continuous%20vertical%20comic%20strip%20flowing%20down%2C%20vibrant%20colors%2C%20dynamic%20action%20scenes%2C%20clean%20modern%20illustration%20style%2C%20immersive%20reading%20experience%20visualization%2C%20warm%20soft%20background&width=1600&height=900&seq=cta-scroll-reading&orientation=landscape"
                alt="韩漫下拉式阅读体验"
                className="w-full h-full object-cover"
              />
            </div>
            <h2 id="cta-heading" className="text-3xl md:text-4xl font-bold text-foreground-950 mb-6 leading-snug" style={{ fontFamily: "var(--font-heading)" }}>
              全新 <span className="italic font-light text-foreground-600">下拉式</span> 阅读，<br />
              <strong className="font-bold">沉浸式</strong> 畅享 <span className="italic font-light text-foreground-600">无删减</span> 韩漫
            </h2>
            <Link
              to={`/category/${categories[0].slug}`}
              className="inline-flex items-center gap-2 px-10 py-4 bg-foreground-950 text-background-50 rounded-full text-base font-semibold hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              立即体验
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-lg"></i>
              </div>
            </Link>
          </div>
        </section>

        {/* Latest Updates */}
        <section id="latest" className="bg-background-100/70 py-14" aria-labelledby="latest-heading">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
              <div>
                <h2 className="text-sm font-semibold text-foreground-600 uppercase tracking-wide mb-2">最新更新</h2>
                <h3 id="latest-heading" className="text-2xl md:text-3xl font-bold text-foreground-950 italic" style={{ fontFamily: "var(--font-heading)" }}>
                  刚刚上新的热门章节
                </h3>
              </div>
              <p className="text-sm text-foreground-600 max-w-md">
                同步韩网更新，第一时间为你呈现最新漫画内容
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-product-shop="">
              {comics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.slug}`}
                  className="group cursor-pointer"
                >
                  <div className="bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:scale-[1.02]">
                    <div className="relative h-[240px] overflow-hidden">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} - 韩漫大全下拉式免费`}
                        title={comic.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      {comic.featured && (
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-[10px] font-semibold rounded-full">热门</span>
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-semibold text-foreground-950 mb-1 group-hover:text-primary-500 transition-colors truncate" style={{ fontFamily: "var(--font-heading)" }}>
                        {comic.title}
                      </h4>
                      <div className="flex items-center justify-between text-xs text-foreground-600">
                        <span>{comic.updateDate}</span>
                        <span>第{comic.chapters}话</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-background-50 py-14" aria-labelledby="faq-heading">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="inline-block w-2 h-2 rounded-full bg-accent-500 mr-2 align-middle"></span>
              <span className="text-xs font-semibold text-foreground-600 uppercase tracking-wide">常见问题</span>
              <h2 id="faq-heading" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-2" style={{ fontFamily: "var(--font-heading)" }}>
                关于韩漫画网你想知道的
              </h2>
              <p className="text-sm text-foreground-600 mt-3">快速了解我们的服务</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-background-100 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    aria-expanded={openFaq === idx}
                  >
                    <span className="text-sm font-semibold text-foreground-950 pr-4">{faq.question}</span>
                    <div className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${openFaq === idx ? "rotate-45" : ""}`}>
                      <i className="ri-add-line text-lg text-foreground-600"></i>
                    </div>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "韩漫画网",
          alternateName: "韩漫大全下拉式免费",
          url: "http://xxjiaxiang.com/",
          description: "韩漫画网支持下拉式免费阅读，韩漫大全漫画无删减下拉式在线观看，界面优化体验舒适，帮助您高效享受韩漫魅力。",
          inLanguage: "zh-CN",
          potentialAction: {
            "@type": "SearchAction",
            target: "http://xxjiaxiang.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "韩漫画网",
          url: "http://xxjiaxiang.com/",
          description: "韩漫画网 - 韩漫大全下拉式免费阅读平台，提供无删减韩漫下拉式在线观看服务",
          contactPoint: {
            "@type": "ContactPoint",
            email: "hello@xxjiaxiang.com",
            contactType: "customer service",
          },
        })}
      </script>
    </>
  );
}